/**
 * ConsolidatedHoldings - Orchestrator for consolidated portfolio analytics
 * TASK-206 Phase 2: Enhanced Analytics
 *
 * Delegates rendering to focused sub-components in ./consolidated/
 */

import React, { useState, useMemo, useEffect } from 'react';
import { ConsolidatedHoldings as ConsolidatedHoldingsData, ConsolidatedPosition } from '../../hooks/useClientLevelData';
import { portfolioApiService } from '../../services/api/portfolioApiService';
import AllocationTable from './molecules/AllocationTable';
import RebalanceProposal from './RebalanceProposal';
import QuickActions from './molecules/QuickActions';
import type { AllocationRow } from './index';
import type { QuickAction } from './molecules/QuickActions';

// Sub-components
import AssetAllocationOverview from './consolidated/AssetAllocationOverview';
import IPSViolationsAlert from './consolidated/IPSViolationsAlert';
import DuplicateHoldingsPanel from './consolidated/DuplicateHoldingsPanel';
import ConsolidatedPositionsTable from './consolidated/ConsolidatedPositionsTable';
import ExposureBreakdowns from './consolidated/ExposureBreakdowns';
import CurrencyRiskAnalysis from './consolidated/CurrencyRiskAnalysis';
import ConcentrationRisksSummary from './consolidated/ConcentrationRisksSummary';

/** The canonical drift payload (TASK-723). Same shape `CompositionTab` reads. */
interface CanonicalDriftSlot {
  asset_class?: string;
  current: number;
  target: number;
  drift: number;
  breach: boolean;
}
interface CanonicalDrift {
  assessment_status?: 'assessed' | 'incomplete';
  message?: string;
  drift_by_asset_class?: Record<string, CanonicalDriftSlot>;
}

export interface ConsolidatedHoldingsProps {
  data: ConsolidatedHoldingsData;
  /** Required to reach the canonical drift classification. Without it no verdict is shown. */
  clientId?: string;
  className?: string;
}

const formatCurrency = (value: number) => {
  if (value >= 1000000) return `CHF ${(value / 1000000).toFixed(2)}M`;
  if (value >= 1000) return `CHF ${(value / 1000).toFixed(0)}K`;
  return `CHF ${value.toLocaleString('de-CH')}`;
};

const ConsolidatedHoldings: React.FC<ConsolidatedHoldingsProps> = ({
  data,
  clientId,
  className = ''
}) => {
  // TASK-877: the slot the advisor asked to reduce. `Decrease` on the IPS table was a control
  // that did nothing — `AllocationTable` degrades it to a plain label when no handler is
  // supplied, which is honest but is the lesser of the two outcomes the ticket allows. This
  // table has a `clientId`, so it can do the thing the word promises: open the sized proposal
  // TASK-868 built, exactly as `CompositionTab` does from the same component.
  const [proposalSlot, setProposalSlot] = useState<string | null>(null);
  const [sortField, setSortField] = useState<keyof ConsolidatedPosition>('value');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  const [expandedPosition, setExpandedPosition] = useState<string | null>(null);
  const [chartType, setChartType] = useState<'pie' | 'bar'>('pie');

  const handleSort = (field: keyof ConsolidatedPosition) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  // Transform allocation data for charts
  const assetAllocationData = Object.entries(data.assetAllocation).map(([name, value]) => ({ name, value }));
  const geographicExposureData = Object.entries(data.geographicExposure).map(([name, value]) => ({ name, value }));
  const sectorExposureData = Object.entries(data.sectorExposure).map(([name, value]) => ({ name, value }));
  const currencyExposureData = Object.entries(data.currencyExposure).map(([name, value]) => ({ name, value }));

  // TASK-723 (review 2026-07-29): this was the THIRD drift implementation, and the one the
  // ticket's "no 5% or 2% constant remains in the frontend" grep missed. It fed the SAME
  // `AllocationTable` molecule as CompositionTab, but classified asset-class deviations
  // against hardcoded 5% / 2% bands — so the consolidated view could show a green "optimal"
  // tick on the very allocation the Radar was raising `portfolio_drift_breach` for. It now
  // reads the same `GET /api/portfolio/drift/{clientId}` CompositionTab does.
  const [driftResult, setDriftResult] = useState<CanonicalDrift | null>(null);
  const [driftState, setDriftState] = useState<'loading' | 'ready' | 'incomplete' | 'failed'>(
    'loading',
  );
  const [driftNote, setDriftNote] = useState<string | null>(null);

  useEffect(() => {
    if (!clientId) {
      setDriftState('incomplete');
      setDriftNote('No client selected.');
      return;
    }
    let cancelled = false;
    setDriftState('loading');
    portfolioApiService
      .getDrift(clientId)
      .then((res: any) => {
        if (cancelled) return;
        const d = res.drift as CanonicalDrift;
        setDriftResult(d);
        setDriftState(d?.assessment_status === 'incomplete' ? 'incomplete' : 'ready');
        setDriftNote(d?.assessment_status === 'incomplete' ? (d?.message ?? null) : null);
      })
      .catch((err: any) => {
        if (cancelled) return;
        // 422 `risk_profile_incomplete` is the correct answer, not a failure — and it must
        // render as "cannot assess", never as a verdict (TASK-722's pattern).
        const detail = err?.response?.data?.detail;
        const incomplete =
          err?.response?.status === 422 || detail?.error === 'risk_profile_incomplete';
        setDriftState(incomplete ? 'incomplete' : 'failed');
        setDriftNote(detail?.message ?? err?.message ?? null);
      });
    return () => {
      cancelled = true;
    };
  }, [clientId]);

  // Straight from the canonical per-slot classification. When drift could not be assessed
  // the table stays empty rather than showing a verdict derived from nothing.
  const allocationTableData: AllocationRow[] = useMemo(() => {
    const slots = driftResult?.drift_by_asset_class;
    if (driftState !== 'ready' || !slots) return [];

    return Object.entries(slots).map(([key, slot]) => ({
      name: key,
      category: slot.asset_class ?? key,
      current: slot.current,
      value: data.totalValue * (slot.current / 100),
      percentage: slot.current,
      target: slot.target,
      drift: slot.drift,
      difference: slot.drift,
      // The canonical breach flag — the client's own tolerance band, decided server-side.
      status: slot.breach ? ('critical' as const) : ('optimal' as const),
      action: !slot.breach
        ? ('maintain' as const)
        : slot.drift > 0
          ? ('decrease' as const)
          : ('increase' as const),
    })) as AllocationRow[];
  }, [driftResult, driftState, data.totalValue]);

  // Generate Quick Actions based on portfolio state
  const quickActions: QuickAction[] = useMemo(() => {
    const actions: QuickAction[] = [];

    // TASK-723 (review): the quick action used its own 5% band, so it could demand
    // "Portfolio Rebalancing Required" for a slot the client's mandate permits — or stay
    // silent on a genuine breach inside a tighter 2% band. `status` is the server-side
    // breach flag; when drift was not assessed there are no rows and no action is proposed.
    const criticalDrift = allocationTableData.find(row => row.status === 'critical');
    if (criticalDrift && criticalDrift.difference) {
      const driftAmount = data.totalValue * Math.abs(criticalDrift.difference) / 100;
      const action = criticalDrift.difference > 0 ? 'Reduce' : 'Add';
      actions.push({
        type: 'rebalance',
        title: 'Portfolio Rebalancing Required',
        description: `${criticalDrift.category} is ${Math.abs(criticalDrift.difference).toFixed(1)}% ${criticalDrift.difference > 0 ? 'above' : 'below'} IPS target. ${action} approximately ${formatCurrency(driftAmount)}.`,
        actionLabel: 'View rebalancing options'
      });
    }

    if (data.duplicates && data.duplicates.length > 0) {
      const totalDuplicateValue = data.duplicates.reduce((sum, dup) => sum + dup.totalValue, 0);
      actions.push({
        type: 'alert',
        title: `${data.duplicates.length} Duplicate Holdings Detected`,
        description: `Total value: ${formatCurrency(totalDuplicateValue)} across multiple portfolios. Consider consolidating to reduce complexity.`,
        actionLabel: 'Review duplicates'
      });
    }

    if (data.concentration && data.concentration.length > 0) {
      const highRisks = data.concentration.filter(c => c.riskLevel === 'HIGH');
      if (highRisks.length > 0) {
        actions.push({
          type: 'alert',
          title: `${highRisks.length} High Concentration Risk${highRisks.length > 1 ? 's' : ''}`,
          description: `${highRisks[0].name} represents ${highRisks[0].percentOfTotal.toFixed(1)}% of total wealth. Recommend reducing to <10%.`,
          actionLabel: 'View concentration risks'
        });
      }
    }

    if (actions.length === 0 && allocationTableData.length > 0) {
      actions.push({
        type: 'success',
        title: 'Portfolio Well Balanced',
        description: 'All allocations are within IPS targets. No immediate action required.',
      });
    }

    return actions.slice(0, 3);
  }, [allocationTableData, data.duplicates, data.concentration, data.totalValue]);

  return (
    <div className={`space-y-6 ${className}`}>
      <AssetAllocationOverview
        data={assetAllocationData}
        currencyData={currencyExposureData}
        chartType={chartType}
        onChartTypeChange={setChartType}
      />

      <IPSViolationsAlert
        allocationTableData={allocationTableData}
        totalValue={data.totalValue}
      />

      {allocationTableData.length > 0 && (
        <div className="bg-white rounded-lg shadow p-6">
          <AllocationTable
            data={allocationTableData}
            title="Allocation vs IPS Targets"
            showTarget={true}
            showDrift={true}
            showValue={true}
            showActions={true}
            showStatus={true}
            currency="CHF"
            onActionClick={
              clientId
                ? (row, action) => {
                    // Only `decrease` is an action; `Increase` and `Maintain` render as labels
                    // and must not reach this even if they somehow did.
                    if (action === 'decrease') setProposalSlot(row.name || row.category);
                  }
                : undefined
            }
          />
          {proposalSlot && clientId && (
            <RebalanceProposal
              clientId={clientId}
              slot={proposalSlot}
              onClose={() => setProposalSlot(null)}
            />
          )}
        </div>
      )}

      {/* TASK-722/723: an unassessable allocation says so. It must never fall through to a
          silent empty area that reads as "nothing is wrong". */}
      {driftState !== 'ready' && driftState !== 'loading' && (
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-sm font-semibold text-gray-900">
            Allocation drift cannot be assessed
          </h3>
          <p className="text-sm text-gray-600 mt-1">
            {driftState === 'incomplete'
              ? (driftNote ?? 'Complete risk-profile section 7 to enable drift analysis.')
              : `The drift service could not be reached${driftNote ? `: ${driftNote}` : '.'}`}
          </p>
        </div>
      )}

      {quickActions.length > 0 && <QuickActions actions={quickActions} />}

      <DuplicateHoldingsPanel duplicates={data.duplicates ?? []} />

      <ConsolidatedPositionsTable
        positions={data.positions}
        totalValue={data.totalValue}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
        expandedPosition={expandedPosition}
        onExpandToggle={(isin) => setExpandedPosition(expandedPosition === isin ? null : isin)}
      />

      <ExposureBreakdowns
        geographicData={geographicExposureData}
        sectorData={sectorExposureData}
        chartType={chartType}
      />

      <CurrencyRiskAnalysis
        currencyData={currencyExposureData}
        totalValue={data.totalValue}
      />

      <ConcentrationRisksSummary concentrationRisks={data.concentration ?? []} />
    </div>
  );
};

export default ConsolidatedHoldings;
